import { Box, Image as ChakraImage, Flex } from "@chakra-ui/react";
import { Container } from "components/Container";
import RichtextAdapter from "modules/storyblok/adapters/RichtextAdapter";
import type { ContentMediaProps, GetStoryblokAssetReturnType } from "../../types";
import { getStoryblokAsset, optimiseStoryblokImage } from "../../utils";

const Image = ({
  assetSource,
  assetAlt,
  mediaStyles,
}: GetStoryblokAssetReturnType & { mediaStyles: ContentMediaProps["mediaStyles"] }) => (
  <ChakraImage
    {...mediaStyles}
    w={{ base: "full", lg: "512px" }}
    src={optimiseStoryblokImage(assetSource)}
    alt={assetAlt}
    order={1}
    mx={{ base: "auto", lg: "unset" }}
    borderRadius="xl"
  />
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Video = ({ assetSource }: Pick<GetStoryblokAssetReturnType, "assetSource">) => (
  <Box order={1} w={{ base: "full", lg: "550px" }} h={{ base: "auto", lg: "334px" }}>
    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
    <iframe
      width="100%"
      height="315"
      style={{ borderRadius: "16px" }}
      src="https://www.youtube.com/embed/u-47P9zm3zY?si=2nNVf1YZhDHc3cd0"
      title="YouTube video player"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    />
  </Box>
);

const Media = ({
  assetSource,
  assetAlt,
  mediaStyles,
}: GetStoryblokAssetReturnType & { mediaStyles: ContentMediaProps["mediaStyles"] }) => {
  const isVideo = !assetSource?.endsWith(".png");
  return isVideo ? (
    <Video assetSource={assetSource} />
  ) : (
    <Image assetSource={assetSource} assetAlt={assetAlt} mediaStyles={mediaStyles} />
  );
};

const setContentStyles = (mediaPosition?: ContentMediaProps["mediaPosition"]) => ({
  contentPosition: { base: -1, lg: mediaPosition === "right" ? 0 : 1 },
  contentCustomStyles: {
    paragraph: {
      fontSize: "md",
      mb: { base: 6, xl: 8 },
      _last: { mb: { base: 8, lg: 0 } },
    },
    heading: {
      mb: { base: 4, xl: 8 },
    },
    link: {
      fontWeight: { base: "normal", lg: "medium" },
      _visited: {},
    },
  },
});

export const ContentMedia = ({
  content,
  mediaPosition = "left",
  storyblokAsset,
  bgImage,
  borderTop = "unset",
  mediaStyles,
}: ContentMediaProps) => {
  const { assetSource, assetAlt } = getStoryblokAsset(storyblokAsset);

  return (
    <Box {...bgImage}>
      <Container>
        <Flex
          borderTop={borderTop}
          align="center"
          justify="space-between"
          flexDir={{ base: "column", lg: "row" }}
          py={{ base: 12, xl: 24 }}
          px={{ base: 4, xl: "unset" }}
        >
          <Media assetSource={assetSource} assetAlt={assetAlt} mediaStyles={mediaStyles} />
          <Box
            w={{ base: "full", lg: "unset" }}
            sx={{ h6: { mb: 6 } }}
            maxW={{ base: "auto", lg: "40%" }}
            order={setContentStyles(mediaPosition).contentPosition}
          >
            <RichtextAdapter content={content} customStyles={setContentStyles().contentCustomStyles} />
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};
